import { MONOLITH_BOARD_STYLE } from "@/components/dashboard/monolithBoardStyle";
import { useAdminUsersQuery } from "@/lib/queries/adminQueries";

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export default function AdminUsersPage() {
  const { data, isLoading, error } = useAdminUsersQuery();
  const users = data?.users ?? [];

  return (
    <main className="mx-auto max-w-6xl px-5 py-10 pb-24 sm:px-8">
      <header className="border-b-[3px] border-neutral-950 pb-8">
        <p className="text-[11px] uppercase tracking-[0.28em] text-neutral-600">Superadmin</p>
        <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight [font-family:var(--color-lab-display),system-ui,sans-serif]">
          Users
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-800">
          A simple list of profile rows created from Supabase Auth users.
        </p>
      </header>

      <section className={`mt-8 overflow-hidden rounded-[28px] ${MONOLITH_BOARD_STYLE.panelClassName}`}>
        <div className="flex items-center justify-between border-b-2 border-neutral-950 px-5 py-4">
          <h2 className="text-sm font-bold uppercase tracking-[0.18em]">Registered users</h2>
          <span className="text-xs font-semibold text-neutral-700">{users.length} total</span>
        </div>

        {isLoading ? (
          <p className="px-5 py-8 text-sm text-neutral-700">Loading users...</p>
        ) : error ? (
          <p className="px-5 py-8 text-sm text-red-800">
            {error instanceof Error ? error.message : "Failed to load users."}
          </p>
        ) : users.length === 0 ? (
          <p className="px-5 py-8 text-sm text-neutral-700">No users found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left text-sm">
              <thead className="bg-[#fff2d9] text-[11px] uppercase tracking-[0.16em] text-neutral-700">
                <tr>
                  <th className="border-b-2 border-neutral-950 px-5 py-3 font-bold">User</th>
                  <th className="border-b-2 border-neutral-950 px-5 py-3 font-bold">Role</th>
                  <th className="border-b-2 border-neutral-950 px-5 py-3 font-bold">Created</th>
                  <th className="border-b-2 border-neutral-950 px-5 py-3 font-bold">ID</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-neutral-950/15 last:border-b-0">
                    <td className="px-5 py-4">
                      <p className="font-semibold text-neutral-950">
                        {user.email ?? "No email"}
                      </p>
                      <p className="mt-1 text-xs text-neutral-700">
                        {user.display_name || "No display name"}
                      </p>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex border-2 border-neutral-950 bg-white px-2 py-1 text-[11px] font-bold uppercase tracking-[0.12em]">
                        {user.staff_role ?? "user"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-neutral-800">{formatDate(user.created_at)}</td>
                    <td className="px-5 py-4 font-mono text-xs text-neutral-700">{user.id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
